// src/components/AuditPanel.tsx
// Журнал аудита ПДн для РКН (152-ФЗ)
// Доступ: superadmin (проверяется на бэкенде)
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, Filter, Search, X, Eye, ChevronLeft, ChevronRight, FileSpreadsheet, FileText } from 'lucide-react';
import api from '../api/client';
import { useToast } from '../contexts/ToastContext';

interface AuditLog {
  _id: string;
  entity: string;
  entityId?: string;
  entityName?: string;
  action: string;
  userId?: string;
  userName?: string;
  userRole?: string;
  userEmail?: string;
  changes?: any;
  ipAddress?: string;
  userAgent?: string;
  method?: string;
  url?: string;
  success: boolean;
  errorMessage?: string;
  createdAt: string;
}

const MODULES = [
  { value: '', label: 'Все модули' },
  { value: 'sales', label: 'CRM Продажи' },
  { value: 'accounting', label: 'Бухгалтерия' },
  { value: 'hrm', label: 'HRM' },
  { value: 'kdp', label: 'КДП' },
  { value: 'recruitment', label: 'Рекрутинг' },
  { value: 'elearning', label: 'E-Learning' },
  { value: 'psych', label: 'Психодиагностика' },
  { value: 'auth', label: 'Авторизация' },
];

const ACTIONS = [
  { value: '', label: 'Все действия' },
  { value: 'create', label: 'Создание' },
  { value: 'view', label: 'Просмотр' },
  { value: 'update', label: 'Изменение' },
  { value: 'delete', label: 'Удаление' },
  { value: 'download', label: 'Скачивание' },
  { value: 'export', label: 'Экспорт' },
  { value: 'approve', label: 'Согласование' },
  { value: 'reject', label: 'Отклонение' },
  { value: 'rework', label: 'Возврат на доработку' },
  { value: 'login', label: 'Вход' },
  { value: 'logout', label: 'Выход' },
];

const AuditPanel: React.FC = () => {
  const { showToast } = useToast();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AuditLog | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<any>(null);

  // Фильтры
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [successOnly, setSuccessOnly] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit };
      if (module) params.module = module;
      if (action) params.action = action;
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
      if (dateTo) params.dateTo = new Date(dateTo + 'T23:59:59').toISOString();
      if (successOnly === 'ok') params.success = 'true';
      if (successOnly === 'fail') params.success = 'false';

      const r = await api.get('/audit/logs', { params });
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, module, action, search, dateFrom, dateTo, successOnly, showToast]);

  useEffect(() => { load(); }, [load]);

  const loadStats = async () => {
    try {
      const r = await api.get('/audit/stats');
      setStats(r.data);
      setShowStats(true);
    } catch (e) { showToast('Ошибка статистики', 'error'); }
  };

  const buildParams = () => {
    const params: any = {};
    if (module) params.module = module;
    if (action) params.action = action;
    if (search) params.search = search;
    if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
    if (dateTo) params.dateTo = new Date(dateTo + 'T23:59:59').toISOString();
    return params;
  };

  const downloadFile = async (format: 'csv' | 'xlsx') => {
    try {
      showToast(`Готовлю ${format.toUpperCase()}...`, 'info');
      const r = await api.get(`/audit/export.${format}`, {
        params: buildParams(),
        responseType: 'blob',
      });

      // ★ Проверяем Content-Type: если пришёл JSON — это ошибка, не XLSX
      const ct = String(r.headers['content-type'] || '').toLowerCase();
      if (ct.includes('application/json')) {
        // Читаем blob как текст, парсим JSON, показываем ошибку
        const text = await r.data.text();
        let errMsg = 'Ошибка экспорта';
        try {
          const j = JSON.parse(text);
          errMsg = j.error || errMsg;
        } catch (e) { /* ignore */ }
        showToast(errMsg, 'error');
        return;
      }

      // Имя файла
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = `audit_${timestamp}.${format}`;

      // Скачивание
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast(`✅ ${format.toUpperCase()} скачан`, 'success');
    } catch (e: any) {
      console.error('Download error:', e);
      // Если это blob с JSON — пробуем прочитать
      let msg = 'Ошибка скачивания';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const j = JSON.parse(text);
          msg = j.error || msg;
        } catch (err) { /* ignore */ }
      } else if (e?.response?.data?.error) {
        msg = e.response.data.error;
      } else if (e?.message) {
        msg = e.message;
      }
      showToast(msg, 'error');
    }
  };

  const exportCSV = () => downloadFile('csv');
  const exportXLSX = () => downloadFile('xlsx');

  const totalPages = Math.ceil(total / limit);

  const actionColor = (action: string) => {
    if (action === 'delete' || action === 'reject') return '#ef4444';
    if (action === 'create' || action === 'approve') return '#10b981';
    if (action === 'update' || action === 'rework') return '#f59e0b';
    if (action === 'view' || action === 'read') return '#64748b';
    return '#2575fc';
  };

  return (
    <div style={{ padding: 4 }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' }}>
            📋 Журнал аудита ПДн
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
            Соответствие 152-ФЗ · Хранение 3 года · Всего записей: <b>{total}</b>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnOutline} title="Обновить">
            <RefreshCw size={14} />
          </button>
          <button onClick={loadStats} style={btnOutline}>
            📊 Статистика
          </button>
          <button onClick={exportCSV} style={btnOutline}>
            <FileText size={14} /> CSV
          </button>
          <button onClick={exportXLSX} style={btnPrimary}>
            <FileSpreadsheet size={14} /> XLSX для РКН
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div style={{
        background: '#fff', borderRadius: 14, padding: 16, marginBottom: 16,
        border: '1px solid #e2e8f0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
      }}>
        <div>
          <label style={labelStyle}>Модуль</label>
          <select value={module} onChange={e => { setModule(e.target.value); setPage(1); }} style={inputStyle}>
            {MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Действие</label>
          <select value={action} onChange={e => { setAction(e.target.value); setPage(1); }} style={inputStyle}>
            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Статус</label>
          <select value={successOnly} onChange={e => { setSuccessOnly(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="all">Все</option>
            <option value="ok">Только успешные</option>
            <option value="fail">Только неуспешные</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Дата с</label>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Дата по</label>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={inputStyle} />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={labelStyle}>Поиск (имя, email, URL)</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Например: alekseyvasilev или SalesDeal"
              style={{ ...inputStyle, paddingLeft: 32 }}
            />
          </div>
        </div>
      </div>

      {/* Таблица */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : logs.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📭</div>
          <div>Записей не найдено</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={thStyle}>Дата и время</th>
                <th style={thStyle}>Пользователь</th>
                <th style={thStyle}>Действие</th>
                <th style={thStyle}>Сущность</th>
                <th style={thStyle}>Имя</th>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>URL</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l._id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => setSelected(l)}>
                  <td style={tdStyle}>
                    {new Date(l.createdAt).toLocaleString('ru-RU')}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{l.userName || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.userEmail}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                      background: `${actionColor(l.action)}15`,
                      color: actionColor(l.action),
                    }}>
                      {l.action}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{l.entity}</div>
                    {l.entityId && (
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>
                        ID: {String(l.entityId).slice(-8)}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.entityName || '—'}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                    {l.ipAddress || '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11, color: '#64748b', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.url}
                  </td>
                  <td style={tdStyle}>
                    <button onClick={(e) => { e.stopPropagation(); setSelected(l); }}
                      style={{ ...btnIconSmall }}>
                      <Eye size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={btnOutline}>
            <ChevronLeft size={14} /> Назад
          </button>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Страница <b>{page}</b> из <b>{totalPages}</b>
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={btnOutline}>
            Вперёд <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Модалка деталей */}
      {selected && (
        <div style={modalOverlayStyle} onClick={() => setSelected(null)}>
          <div style={{ ...modalContentStyle, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                Детали записи аудита
              </h3>
              <button onClick={() => setSelected(null)} style={btnIconSmall}><X size={16} /></button>
            </div>
            <div style={{ padding: 24, maxHeight: '65vh', overflow: 'auto' }}>
              <Field label="Дата и время" value={new Date(selected.createdAt).toLocaleString('ru-RU')} />
              <Field label="Пользователь" value={`${selected.userName || '—'} (${selected.userEmail || '—'})`} />
              <Field label="Роль" value={selected.userRole || '—'} />
              <Field label="Действие" value={selected.action} />
              <Field label="Сущность" value={selected.entity} />
              <Field label="Имя сущности" value={selected.entityName || '—'} />
              <Field label="ID сущности" value={String(selected.entityId || '—')} mono />
              <Field label="Статус" value={selected.success ? '✅ Успех' : '❌ Ошибка'} />
              {selected.errorMessage && <Field label="Ошибка" value={selected.errorMessage} />}
              <Field label="IP" value={selected.ipAddress || '—'} mono />
              <Field label="Метод" value={selected.method || '—'} />
              <Field label="URL" value={selected.url || '—'} mono />
              <Field label="User-Agent" value={selected.userAgent || '—'} mono small />
              {selected.changes && (selected.changes.after || selected.changes.before) && (
                <div style={{ marginTop: 12 }}>
                  <div style={labelStyle}>Изменения</div>
                  <pre style={{
                    background: '#f8fafc', padding: 12, borderRadius: 10,
                    fontSize: 11, fontFamily: 'monospace', overflow: 'auto', maxHeight: 300,
                  }}>
                    {JSON.stringify(selected.changes, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модалка статистики */}
      {showStats && stats && (
        <div style={modalOverlayStyle} onClick={() => setShowStats(false)}>
          <div style={{ ...modalContentStyle, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>📊 Статистика за 30 дней</h3>
              <button onClick={() => setShowStats(false)} style={btnIconSmall}><X size={16} /></button>
            </div>
            <div style={{ padding: 24, maxHeight: '65vh', overflow: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ background: '#f0f9ff', padding: 16, borderRadius: 12 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Всего записей</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#2575fc' }}>{stats.total}</div>
                </div>
                <div style={{ background: '#fef2f2', padding: 16, borderRadius: 12 }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>С ошибками</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{stats.failed}</div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>По действиям</div>
                {(stats.byAction || []).slice(0, 10).map((x: any) => (
                  <div key={x._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span>{x._id}</span>
                    <b>{x.count}</b>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>По сущностям</div>
                {(stats.byEntity || []).slice(0, 10).map((x: any) => (
                  <div key={x._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span>{x._id}</span>
                    <b>{x.count}</b>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Активные пользователи</div>
                {(stats.byUser || []).slice(0, 10).map((x: any, i: number) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span>{x._id?.userName || x._id?.userEmail || '—'}</span>
                    <b>{x.count}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean; small?: boolean }> = ({ label, value, mono, small }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: small ? 11 : 13, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
  </div>
);

const thStyle: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'top' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' };
const btnPrimary: React.CSSProperties = { background: 'linear-gradient(135deg, #2575fc, #6a11cb)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit' };
const btnOutline: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#475569', fontFamily: 'inherit' };
const btnIconSmall: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b', borderRadius: 6 };
const modalOverlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 };
const modalContentStyle: React.CSSProperties = { background: '#fff', borderRadius: 18, width: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(15,23,42,0.3)', overflow: 'hidden' };
const modalHeaderStyle: React.CSSProperties = { padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };

export default AuditPanel;
